import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  #controller;
  #queue = Promise.resolve();
  #refreshPending;
  #mutationPending = false;
  #stale = false;
  #rails = new Map();
  #resizeFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    window.addEventListener('resize', this.#handleResize, { signal, passive: true });
    window.addEventListener('pointerup', this.#releaseRails, { signal, passive: true });
    window.addEventListener('pointercancel', this.#releaseRails, { signal, passive: true });
    this.#initializeRails();
    if (this.hasAttribute('data-mcx-editor-preview')) this.showDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#clearRails();
    cancelAnimationFrame(this.#resizeFrame);
  }

  get #content() {
    return this.querySelector('[data-mcx-cart-content]');
  }

  get #root() {
    const root = this.#content?.dataset.root || window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/+$/, '')}/`;
  }

  get #errorMessage() {
    return this.dataset.mcxErrorMessage || 'Unable to update your cart. Please try again.';
  }

  #enqueue(operation) {
    const result = this.#queue.then(operation, operation);
    this.#queue = result.catch(() => {});
    return result;
  }

  #reportError(error, syncFailure = false) {
    const target = this.querySelector('[data-mcx-cart-error]');
    if (target) {
      target.textContent = syncFailure
        ? this.dataset.mcxSyncErrorMessage || 'Your cart may have changed. Open your cart to review it.'
        : error?.message || this.#errorMessage;
    }
    const recovery = this.querySelector('[data-mcx-cart-recovery]');
    if (recovery) recovery.hidden = !syncFailure;
  }

  #clearError() {
    const target = this.querySelector('[data-mcx-cart-error]');
    if (target) target.textContent = '';
    const recovery = this.querySelector('[data-mcx-cart-recovery]');
    if (recovery) recovery.hidden = true;
  }

  async #requestJSON(path, options = {}) {
    const response = await fetch(`${this.#root}${path}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
    });
    let data;
    try {
      data = await response.json();
    } catch (_) {
      throw new Error(this.#errorMessage);
    }
    if (!response.ok || Number(data?.status) >= 400) {
      const message = data?.description || data?.message;
      throw new Error(typeof message === 'string' ? message : this.#errorMessage);
    }
    return data;
  }

  async #fetchSection(sectionId) {
    if (!sectionId) throw new Error(this.#errorMessage);
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(this.#errorMessage);
    return response.text();
  }

  #parseContent(markup) {
    if (typeof markup !== 'string' || !markup.trim()) return null;
    return new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-mcx-cart-content]');
  }

  #captureFocus(content) {
    const active = document.activeElement;
    if (!content.contains(active)) return null;
    const line = active.closest('[data-mcx-cart-line]');
    return {
      id: active.id,
      key: line?.dataset.lineKey,
      quantity: active.getAttribute('data-mcx-quantity-change'),
      remove: active.hasAttribute('data-mcx-remove'),
    };
  }

  #restoreFocus(previous) {
    if (!previous || !this.querySelector('dialog')?.open) return;
    let target = previous.id ? document.getElementById(previous.id) : null;
    if (!target && previous.key) {
      const line = [...this.querySelectorAll('[data-mcx-cart-line]')].find(item => item.dataset.lineKey === previous.key);
      if (line && previous.quantity !== null) {
        target = [...line.querySelectorAll('[data-mcx-quantity-change]')].find(button => button.dataset.mcxQuantityChange === previous.quantity);
      } else if (line && previous.remove) {
        target = line.querySelector('[data-mcx-remove]');
      }
    }
    if (!target || target.disabled) target = this.querySelector('[ref="closeButton"]');
    target?.focus({ preventScroll: true });
  }

  #publishCart(cart) {
    if (!cart || !Number.isFinite(cart.item_count)) return;
    this.dispatchEvent(new CartUpdateEvent(cart, 'mcx-cart', {
      source: 'mcx-cart',
      itemCount: cart.item_count,
    }));
  }

  async #refresh({ cart, sections } = {}) {
    const content = this.#content;
    if (!content) return null;
    const sectionId = content.dataset.sectionId;
    const validCart = cart && Array.isArray(cart.items) && Number.isFinite(cart.item_count);
    const suppliedContent = this.#parseContent(sections?.[sectionId]);
    const [cartResult, sectionResult] = await Promise.allSettled([
      validCart ? Promise.resolve(cart) : this.#requestJSON('cart.js'),
      suppliedContent ? Promise.resolve(suppliedContent) : this.#fetchSection(sectionId).then(markup => this.#parseContent(markup)),
    ]);
    const currentCart = cartResult.status === 'fulfilled' ? cartResult.value : null;
    if (!this.isConnected) return currentCart;
    const nextContent = sectionResult.status === 'fulfilled' ? sectionResult.value : null;
    if (!currentCart || !nextContent) {
      this.#stale = true;
      this.#publishCart(currentCart);
      this.#reportError(null, true);
      return null;
    }
    const focus = this.#captureFocus(content);
    const linesScroll = content.querySelector('[data-mcx-cart-lines]')?.scrollTop || 0;
    this.#clearRails();
    content.replaceChildren(...nextContent.childNodes);
    this.#stale = false;
    this.#clearError();
    const lines = content.querySelector('[data-mcx-cart-lines]');
    if (lines) lines.scrollTop = linesScroll;
    this.#initializeRails();
    this.#publishCart(currentCart);
    this.#restoreFocus(focus);
    return currentCart;
  }

  #refreshQueued(options) {
    return this.#enqueue(() => this.#refresh(options));
  }

  #refreshOnOpen() {
    if (!this.#refreshPending) {
      this.#refreshPending = this.#refreshQueued()
        .catch(error => this.#reportError(error, true))
        .finally(() => { this.#refreshPending = null; });
    }
    return this.#refreshPending;
  }

  #handleCartUpdate = event => {
    const data = event.detail?.data || {};
    if (data.source === 'mcx-cart' || event.detail?.sourceId === 'mcx-cart') return;
    if (this.hasAttribute('auto-open') && event instanceof CartAddEvent && !data.didError) this.showDialog();
    this.#refreshQueued({
      cart: this.#mutationPending ? null : event.detail?.resource,
      sections: this.#mutationPending ? null : data.sections,
    })
      .catch(error => this.#reportError(error, true));
  };

  #mutationControls() {
    return [...this.querySelectorAll('[data-mcx-quantity-change], [data-mcx-remove], [data-mcx-upsell-form] button[type="submit"], [data-mcx-upsell-variant]')];
  }

  async #mutate(path, payload) {
    if (this.#mutationPending) return;
    this.#mutationPending = true;
    this.#clearError();
    this.#content?.setAttribute('aria-busy', 'true');
    const controls = this.#mutationControls().map(control => [control, control.disabled]);
    controls.forEach(([control]) => { control.disabled = true; });
    try {
      await this.#enqueue(async () => {
        if (this.#stale) {
          await this.#refresh();
          return;
        }
        const sectionId = this.#content?.dataset.sectionId;
        const request = { ...payload };
        if (sectionId) {
          request.sections = [sectionId];
          request.sections_url = window.location.pathname;
        }
        let response;
        try {
          response = await this.#requestJSON(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
          });
        } catch (error) {
          const recovered = await this.#refresh();
          if (recovered) this.#reportError(error);
          return;
        }
        await this.#refresh({ cart: path === 'cart/change.js' ? response : null, sections: response.sections });
      });
    } catch (error) {
      this.#stale = true;
      this.#reportError(error, true);
    } finally {
      this.#mutationPending = false;
      this.#content?.removeAttribute('aria-busy');
      controls.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
    }
  }

  #handleClick = event => {
    const target = event.target instanceof Element ? event.target : null;
    const arrow = target?.closest('[data-mcx-upsell-direction]');
    if (arrow) {
      const rail = arrow.closest('[data-mcx-upsells]')?.querySelector('[data-mcx-upsell-rail]');
      const state = this.#rails.get(rail);
      if (state) state.goTo(state.index + Number(arrow.dataset.mcxUpsellDirection));
      return;
    }
    const quantityButton = target?.closest('[data-mcx-quantity-change]');
    const remove = target?.closest('[data-mcx-remove]');
    if (!quantityButton && !remove) return;
    event.preventDefault();
    if (this.#mutationPending) return;
    const line = (quantityButton || remove).closest('[data-mcx-cart-line]');
    if (!line?.dataset.lineKey) return;
    const current = Number(line.dataset.quantity);
    const minimum = Math.max(1, Number(line.dataset.quantityMin) || 1);
    const increment = Math.max(1, Number(line.dataset.quantityStep) || 1);
    const maximum = line.hasAttribute('data-quantity-max') ? Number(line.dataset.quantityMax) : Infinity;
    const direction = remove ? 0 : Number(quantityButton.dataset.mcxQuantityChange);
    if (!remove && direction > 0 && current >= maximum) return;
    let quantity = remove ? 0 : current + direction * increment;
    quantity = quantity < minimum ? 0 : Math.min(quantity, maximum);
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity === current) return;
    this.#mutate('cart/change.js', { id: line.dataset.lineKey, quantity });
  };

  #handleChange = event => {
    const select = event.target.closest('[data-mcx-upsell-variant]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-mcx-upsell-slide]');
    if (!option || !card) return;
    const price = card.querySelector('[data-mcx-upsell-price]');
    if (price) price.textContent = option.dataset.price || '';
    const quantity = card.querySelector('input[name="quantity"]');
    if (quantity) quantity.value = option.dataset.quantity || '1';
    const error = card.querySelector('[data-mcx-upsell-error]');
    if (error) error.textContent = '';
  };

  #handleSubmit = async event => {
    const form = event.target.closest('form[data-mcx-upsell-form]');
    if (!form || event.defaultPrevented) return;
    event.preventDefault();
    if (this.#mutationPending) return;
    const option = form.querySelector('[data-mcx-upsell-variant]')?.selectedOptions[0];
    const button = form.querySelector('button[type="submit"]');
    const id = Number(option?.value);
    const quantity = Number(option?.dataset.quantity || 1);
    if (!button || !Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) return;
    const originalText = button.textContent;
    button.textContent = form.dataset.addingLabel || originalText;
    button.setAttribute('aria-busy', 'true');
    try {
      await this.#mutate('cart/add.js', { items: [{ id, quantity }] });
    } finally {
      if (button.isConnected) {
        button.textContent = form.dataset.addLabel || originalText;
        button.removeAttribute('aria-busy');
      }
    }
  };

  #clearRails() {
    for (const state of this.#rails.values()) {
      clearTimeout(state.timer);
      state.controller.abort();
    }
    this.#rails.clear();
  }

  #initializeRails() {
    this.querySelectorAll('[data-mcx-upsell-rail]').forEach(rail => {
      if (this.#rails.has(rail)) return;
      const section = rail.closest('[data-mcx-upsells]');
      const slides = [...rail.querySelectorAll('[data-mcx-upsell-slide]')].slice(0, 6);
      if (!section || !slides.length) return;
      const state = { index: 0, timer: null, touching: false, controller: new AbortController() };
      const { signal } = state.controller;
      const positions = () => slides.map(slide => slide.offsetLeft - slides[0].offsetLeft);
      const nearest = () => positions().reduce((best, offset, index, offsets) => Math.abs(offset - rail.scrollLeft) < Math.abs(offsets[best] - rail.scrollLeft) ? index : best, 0);
      const controls = () => {
        const previous = section.querySelector('[data-mcx-upsell-direction="-1"]');
        const next = section.querySelector('[data-mcx-upsell-direction="1"]');
        if (previous) previous.disabled = state.index === 0;
        if (next) next.disabled = state.index === slides.length - 1;
        const status = section.querySelector('[data-mcx-upsell-status]');
        if (status) status.textContent = (section.dataset.slideStatus || '').replace('__CURRENT__', String(state.index + 1)).replace('__COUNT__', String(slides.length));
      };
      state.goTo = (index, instant = false) => {
        state.index = Math.max(0, Math.min(slides.length - 1, index));
        rail.scrollTo({ left: positions()[state.index], behavior: instant || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        controls();
      };
      state.settle = () => {
        if (state.touching || !rail.isConnected || !rail.clientWidth) return;
        state.index = nearest();
        const target = positions()[state.index];
        if (Math.abs(rail.scrollLeft - target) > 1) rail.scrollTo({ left: target, behavior: 'instant' });
        controls();
      };
      const schedule = () => {
        clearTimeout(state.timer);
        state.timer = setTimeout(state.settle, 160);
      };
      rail.addEventListener('scroll', schedule, { signal, passive: true });
      rail.addEventListener('scrollend', state.settle, { signal });
      rail.addEventListener('pointerdown', () => { state.touching = true; }, { signal, passive: true });
      rail.addEventListener('keydown', event => {
        if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : state.index + (event.key === 'ArrowRight' ? 1 : -1);
        state.goTo(index);
      }, { signal });
      this.#rails.set(rail, state);
      controls();
    });
  }

  #releaseRails = () => {
    for (const state of this.#rails.values()) {
      if (!state.touching) continue;
      state.touching = false;
      clearTimeout(state.timer);
      state.timer = setTimeout(state.settle, 160);
    }
  };

  #handleResize = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => {
      for (const [rail, state] of this.#rails) {
        if (rail.isConnected && rail.clientWidth) state.goTo(state.index, true);
      }
    });
  };

  open() {
    this.showDialog();
    this.#refreshOnOpen();
    this.#handleResize();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      if (!this.isConnected) return;
      document.querySelector('shopify-payment-terms')?.shadowRoot
        ?.querySelector('#shopify-installments-cta')
        ?.addEventListener('click', () => this.closeDialog(), { once: true, signal: this.#controller.signal });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
